/**
 * Raw chip rows for a checkpoint not already covered by selectedReading / additionalOptions.
 * Used by CheckpointEditor supplements (shared dedupe rules).
 */

import { translateReasonCode, translateTimelineDetailsToRu } from './reason-codes'

export const ABS_MATCH_SLACK_MS = 2500

function parseReadingInstantMs(ts) {
  if (ts == null || ts === '') return null
  const s = String(ts).trim()
  if (s.includes('T') || (s.length >= 10 && s[4] === '-' && s[7] === '-')) {
    const ms = Date.parse(s)
    return Number.isNaN(ms) ? null : ms
  }
  return null
}

function readingAbsoluteMs(r) {
  if (!r) return null
  const raw = r.absoluteTimestamp ?? r.timestamp
  return parseReadingInstantMs(raw)
}

function normalizePointName(p) {
  return String(p || '').trim().toUpperCase()
}

function lapMatchesCheckpoint(raw, cpLap) {
  const rawLap = raw.lap || 0
  const cLap = cpLap || 0
  if (raw.lap == null || raw.lap === '') return true
  return cLap === rawLap
}

function optAbsoluteMs(opt) {
  if (!opt || !opt.absoluteTimestamp) return null
  const ms = Date.parse(String(opt.absoluteTimestamp))
  return Number.isNaN(ms) ? null : ms
}

function formatMainLineTimestampRaw(raw) {
  const s = raw && raw.timestamp != null ? String(raw.timestamp).trim() : ''
  if (!s) return '—'
  if (s.includes('T') || (s.length >= 10 && s[4] === '-' && s[7] === '-')) {
    try {
      const d = new Date(s)
      if (!Number.isNaN(d.getTime())) {
        const hh = String(d.getUTCHours()).padStart(2, '0')
        const mm = String(d.getUTCMinutes()).padStart(2, '0')
        const ss = String(d.getUTCSeconds()).padStart(2, '0')
        const ms = String(d.getUTCMilliseconds()).padStart(3, '0')
        return `${hh}:${mm}:${ss}.${ms}`
      }
    } catch (e) {
      /* ignore */
    }
  }
  return s
}

function mapRawRowReason(raw) {
  const details = String(raw && raw.timelineReasonDetails ? raw.timelineReasonDetails : '').trim()
  const fromDetails = details ? translateTimelineDetailsToRu(details) : ''
  if (fromDetails) return fromDetails
  const reasonCode = String(raw && raw.timelineReasonCode ? raw.timelineReasonCode : '').trim()
  if (reasonCode) {
    const fromCode = translateReasonCode(reasonCode, '')
    if (fromCode) return fromCode
  }
  const reason = String(raw && raw.timelineReason ? raw.timelineReason : '').trim()
  if (!reason) return ''
  const reasonMap = {
    OutOfWindow: 'Вне окна времени участника',
    Duplicate: 'Дубликат',
    TooEarly: 'Слишком рано',
    TooLate: 'Слишком поздно',
    Selected: 'Выбрано анализатором',
    NoCheckpointMatch: 'Нет совпадения с чекпоинтом',
  }
  return reasonMap[reason] || reason
}

export function buildRawSupplementOptionsForCheckpoint(participant, checkpoint) {
  if (!participant || !checkpoint) return []
  const rawReadings = participant.rawReadings || []
  if (!rawReadings.length) return []

  const wantPoint = normalizePointName(checkpoint.point)
  const cpLap = checkpoint.lap || 0
  const knownAbsMs = []

  const pushAbs = ms => {
    if (ms == null || Number.isNaN(ms)) return
    knownAbsMs.push(ms)
  }

  if (checkpoint.selectedReading) pushAbs(optAbsoluteMs(checkpoint.selectedReading));
  (checkpoint.additionalOptions || []).forEach(o => {
    pushAbs(optAbsoluteMs(o))
  })

  const coversKnown = rawMs => {
    if (rawMs == null) return false
    return knownAbsMs.some(k => Math.abs(k - rawMs) <= ABS_MATCH_SLACK_MS)
  }

  const usedRawSecondKeys = new Set()
  const out = []

  rawReadings.forEach(raw => {
    if (normalizePointName(raw.point) !== wantPoint) return
    if (!lapMatchesCheckpoint(raw, cpLap)) return

    const rawAbs = readingAbsoluteMs(raw)
    if (coversKnown(rawAbs)) return

    if (rawAbs != null) {
      const secKey = String(Math.floor(rawAbs / 1000))
      if (usedRawSecondKeys.has(secKey)) return
      usedRawSecondKeys.add(secKey)
    }

    const tr = String(raw.timelineReason || '').trim()
    if (tr === 'Selected' && checkpoint.selectedReading) {
      const selAbs = optAbsoluteMs(checkpoint.selectedReading)
      if (selAbs != null && rawAbs != null && Math.abs(selAbs - rawAbs) <= ABS_MATCH_SLACK_MS) {
        return
      }
    }

    const ts = raw.timestamp
    out.push({
      timestamp: formatMainLineTimestampRaw(raw),
      absoluteTimestamp: typeof ts === 'string' && ts.includes('T') ? ts : undefined,
      splitTime: '—',
      reason: mapRawRowReason(raw),
      reasonCode: raw.timelineReasonCode || undefined,
      epc: raw.epc,
      code: raw.code,
      num: raw.num,
      rssi: raw.rssi != null ? String(raw.rssi) : undefined,
      antena: raw.antena,
      point: raw.point,
      fromRawOnly: true,
    })
  })
  return out
}
